import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApiQuery, useAuth, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { projects } from '../../api/fee'
import type { Project } from '../../api/types'
import { Notice, PageTitle, QueryState } from '../../components/common'
import { useMoneyAction } from '../../lib/useMoneyAction'

/**
 * Port of `ProjectListComponent` (`/projects`, where `''` redirects): the
 * projects (tenants) the caller belongs to (`GET /projects`), a client-side
 * name filter, and "Start new project" (`POST /projects {name}`). Legacy
 * showed the start-project card only to `@webbandflow.co.uk` accounts — a
 * client-side e-mail check with no backend counterpart (the backend lets any
 * signed-in user create a project), kept as-is for parity.
 */
export function ProjectListPage() {
  const username = useAuth().user?.username ?? ''
  const q = useApiQuery(() => projects.search(), [])
  const [filter, setFilter] = useState('')
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const actions = useMoneyAction()
  const canStartProject = username.toLowerCase().includes('@webbandflow.co.uk')

  const rows = useMemo(
    () => (q.data ?? []).filter((p) => !filter || p.name.toLowerCase().includes(filter.toLowerCase())),
    [q.data, filter],
  )
  const columns: Column<Project>[] = [
    { key: 'name', label: 'Project', format: (_v, p) => <Link to={`/projects/${encodeURIComponent(p.id)}`}>{p.name}</Link> },
    { key: 'id', label: 'ID', align: 'right' },
  ]

  const add = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    const r = await actions.run('add', trimmed, () => projects.add(trimmed))
    if (r) {
      setName('')
      setAdding(false)
      q.refetch()
    }
  }

  return (
    <div className="portal-card">
      <PageTitle title="Projects">
        <input
          type="search"
          aria-label="Search projects"
          placeholder="Search…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <button type="button" onClick={() => q.refetch()}>
          Refresh
        </button>
      </PageTitle>
      <QueryState loading={q.loading} error={q.error}>
        <div className="portal-table-scroll">
          <Table columns={columns} rows={rows} rowKey={(p) => p.id} initialSort={{ key: 'name', dir: 'asc' }} />
        </div>
        {q.data?.length === 0 && <p className="portal-text-muted">You are not a member of any project yet.</p>}
      </QueryState>
      {canStartProject && (
        <div className="fs-panel">
          {!adding ? (
            <button type="button" onClick={() => setAdding(true)}>
              Start new project
            </button>
          ) : (
            <form
              className="portal-toolbar"
              onSubmit={(e) => {
                e.preventDefault()
                void add()
              }}
            >
              <input aria-label="Project name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Project name" />
              <button type="submit" disabled={actions.isBusy('add') || !name.trim()}>
                Add
              </button>
            </form>
          )}
          <Notice feedback={actions.feedbackFor('add')} />
        </div>
      )}
    </div>
  )
}
